import axios from "axios";

const baseURL = process.env.REACT_APP_API_BASE_URL;
let centrosCustoCache = null;
let centrosCustoRequest = null;

const getToken = () => {
  const rawToken =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("jwtToken") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token");

  if (!rawToken) {
    throw new Error("Token nao encontrado.");
  }

  return rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
};

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const getApiErrorMessage = (err, fallbackMessage) => {
  const responseData = err?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) return responseData;
  if (responseData?.mensagem) return responseData.mensagem;
  if (responseData?.message) return responseData.message;
  if (responseData?.erro) return responseData.erro;
  if (responseData?.detail) return responseData.detail;

  if (err?.response?.status === 401) return "Sessao expirada ou token invalido.";
  if (err?.response?.status === 403) return "Cliente sem acesso ativo ao produto Cobranca.";

  return err?.message || fallbackMessage;
};

const normalizeRegra = (regra) => ({
  id: regra?.id ?? null,
  diasAtraso: Number(regra?.diasAtraso || 0),
  templateWhatsappNome: regra?.templateWhatsappNome || "",
  templateWhatsappIdioma: regra?.templateWhatsappIdioma || "pt_BR",
  ativo: Boolean(regra?.ativo),
});

const sortRegras = (regras) => [...regras].sort((a, b) => a.diasAtraso - b.diasAtraso);

const normalizeResumoElegivel = (resumo) => ({
  diasAtraso: Number(resumo?.diasAtraso || 0),
  totalEnvios: Number(resumo?.totalEnvios || 0),
  totalClientes: Number(resumo?.totalClientes || 0),
  totalTitulos: Number(resumo?.totalTitulos || 0),
  totalParcelas: Number(resumo?.totalParcelas || 0),
  valorTotal: Number(resumo?.valorTotal || 0),
});

const normalizeElegivel = (elegivel) => ({
  erpOrigem: elegivel?.erpOrigem || "",
  companyId: elegivel?.companyId || "",
  clientId: elegivel?.clientId || "",
  clientName: elegivel?.clientName || "",
  contatoSelecionado: {
    tipoContato: elegivel?.contatoSelecionado?.tipoContato || "",
    nome: elegivel?.contatoSelecionado?.nome || "",
    tipoDocumento: elegivel?.contatoSelecionado?.tipoDocumento || "",
    documento: elegivel?.contatoSelecionado?.documento || "",
    email: elegivel?.contatoSelecionado?.email || "",
    telefone: elegivel?.contatoSelecionado?.telefone || "",
    telefoneTipo: elegivel?.contatoSelecionado?.telefoneTipo || "",
  },
  valorTotal: Number(elegivel?.valorTotal || 0),
  quantidadeTitulos: Number(elegivel?.quantidadeTitulos || 0),
  quantidadeParcelas: Number(elegivel?.quantidadeParcelas || 0),
  centrosCusto: Array.isArray(elegivel?.centrosCusto) ? elegivel.centrosCusto : [],
  parcelas: Array.isArray(elegivel?.parcelas) ? elegivel.parcelas : [],
});

const normalizeCentroCusto = (centroCusto) => ({
  companyId: centroCusto?.companyId || "",
  costCenterId: centroCusto?.costCenterId || "",
  nome: centroCusto?.nome || "",
  cnpj: centroCusto?.cnpj || null,
});

const getResponsePayload = (data) => {
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }

  return data?.data || data || {};
};

const normalizeTemplate = (template) => ({
  id: template?.id || "",
  name: template?.name || "",
  language: template?.language || "",
  status: template?.status || "",
  category: template?.category || "",
  qualityScore: template?.qualityScore || "",
  rejectedReason: template?.rejectedReason || null,
  components: Array.isArray(template?.components) ? template.components : [],
});

const getRegras = async () => {
  const response = await axios.get(`${baseURL}/produto/cobranca/notificacoes/regras`, {
    headers: authHeaders(),
  });

  const regras = Array.isArray(response?.data?.regras) ? response.data.regras : [];
  return sortRegras(regras.map(normalizeRegra));
};

const saveRegras = async (regras) => {
  const payload = {
    regras: regras.map((regra) => ({
      diasAtraso: Number(regra.diasAtraso),
      templateWhatsappNome: regra.templateWhatsappNome.trim(),
      templateWhatsappIdioma: regra.templateWhatsappIdioma.trim(),
    })),
  };

  const response = await axios.put(`${baseURL}/produto/cobranca/notificacoes/regras`, payload, {
    headers: authHeaders(),
  });

  const responseRegras = Array.isArray(response?.data?.regras) ? response.data.regras : [];
  return sortRegras(responseRegras.map(normalizeRegra));
};

const getTemplates = async ({ limit = 50, after = null } = {}) => {
  const response = await axios.get(`${baseURL}/produto/cobranca/agente/whatsapp/templates`, {
    params: {
      limit,
      ...(after ? { after } : {}),
    },
    headers: authHeaders(),
  });

  return {
    data: Array.isArray(response?.data?.data) ? response.data.data.map(normalizeTemplate) : [],
    after: response?.data?.after || null,
    before: response?.data?.before || null,
  };
};

const getElegiveisResumo = async (diasAtraso) => {
  const diasNormalizados = [...new Set(
    (Array.isArray(diasAtraso) ? diasAtraso : [])
      .map((dia) => Number(dia))
      .filter((dia) => Number.isInteger(dia) && dia >= 1 && dia <= 730)
  )].sort((first, second) => first - second);

  if (!diasNormalizados.length) return [];

  const response = await axios.get(`${baseURL}/produto/cobranca/notificacoes/elegiveis/resumo`, {
    params: { diasAtraso: diasNormalizados.join(",") },
    headers: authHeaders(),
  });

  const payload = getResponsePayload(response?.data);

  return Array.isArray(payload?.dias) ? payload.dias.map(normalizeResumoElegivel) : [];
};

const getElegiveis = async ({ diasAtraso = null, costCenterId = "" } = {}) => {
  const hasDayFilter = diasAtraso !== null && diasAtraso !== undefined && String(diasAtraso).trim() !== "";
  const dia = hasDayFilter ? Number(diasAtraso) : null;
  const centroCusto = String(costCenterId || "").trim();

  if (hasDayFilter && (!Number.isInteger(dia) || dia < 1 || dia > 730)) {
    throw new Error("Informe dias de atraso entre 1 e 730.");
  }

  const response = await axios.get(`${baseURL}/produto/cobranca/notificacoes/elegiveis`, {
    params: {
      ...(hasDayFilter ? { diasAtraso: dia } : {}),
      ...(centroCusto ? { costCenterId: centroCusto } : {}),
    },
    headers: authHeaders(),
  });

  const payload = getResponsePayload(response?.data);

  if (!Array.isArray(payload?.elegiveis)) {
    throw new Error("A resposta de elegíveis não possui a lista de contatos.");
  }

  return {
    diasAtraso: payload.diasAtraso === null || payload.diasAtraso === undefined ? null : Number(payload.diasAtraso),
    costCenterId: payload.costCenterId === null || payload.costCenterId === undefined ? null : String(payload.costCenterId),
    totalClientes: Number(payload.totalClientes || 0),
    totalTitulos: Number(payload.totalTitulos || 0),
    totalParcelas: Number(payload.totalParcelas || 0),
    valorTotal: Number(payload.valorTotal || 0),
    elegiveis: payload.elegiveis.map(normalizeElegivel),
  };
};

const getCentrosCusto = async () => {
  if (centrosCustoCache) return centrosCustoCache;

  if (!centrosCustoRequest) {
    centrosCustoRequest = axios.get(`${baseURL}/empresa/centros-custo`, {
      headers: authHeaders(),
    })
      .then((response) => {
        const payload = getResponsePayload(response?.data);
        const centrosCusto = Array.isArray(payload) ? payload : [];
        centrosCustoCache = centrosCusto.map(normalizeCentroCusto);
        return centrosCustoCache;
      })
      .finally(() => {
        centrosCustoRequest = null;
      });
  }

  return centrosCustoRequest;
};

const createTemplate = async (template) => {
  const payload = {
    name: template.name.trim(),
    language: template.language,
    category: template.category,
    allowCategoryChange: Boolean(template.allowCategoryChange),
    components: template.components,
  };

  const response = await axios.post(`${baseURL}/produto/cobranca/agente/whatsapp/templates`, payload, {
    headers: authHeaders(),
  });

  return response.data;
};

const cobrancaNotificacoesService = {
  createTemplate,
  getElegiveisResumo,
  getElegiveis,
  getCentrosCusto,
  getApiErrorMessage,
  getRegras,
  getTemplates,
  saveRegras,
};

export default cobrancaNotificacoesService;
